import React, { useState, useEffect, useRef } from 'react';
import {
  Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Card, CardBody, Popover, PopoverHeader, PopoverBody, Row, Col
} from 'reactstrap';
import DatePicker from 'react-datepicker';

import {
  startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subDays, subWeeks, subMonths, isEqual, format
} from 'date-fns';

const DateRangePresetFilter = ({
  startDate,
  endDate,
  period,
  onDateRangeChange,
  onPeriodChange,
  usePopoverForCustomRange = true
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('This Month');
  const [showCustomRangePickers, setShowCustomRangePickers] = useState(false);
  const [customRangePopoverOpen, setCustomRangePopoverOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const dropdownToggleRef = useRef(null);

  // Sync local state with props
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  const toggleDropdown = () => {
    return setDropdownOpen(prevState => !prevState);
  };

  const applyPreset = (preset) => {
    let newStartDate = null;
    let newEndDate = null;
    let newPeriod = 'daily';
    const today = new Date();


    if (preset === 'Custom Range') {
      setSelectedPreset('Custom Range');
      if (usePopoverForCustomRange) {
        setCustomRangePopoverOpen(true);
        setShowCustomRangePickers(false);

      } else {
        setShowCustomRangePickers(true);
        setCustomRangePopoverOpen(false);
      }
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(false);

    switch (preset) {
      case 'Today':
        newStartDate = startOfDay(today);
        newEndDate = endOfDay(today);
        newPeriod = 'daily';
        break;
      case 'Yesterday':
        newStartDate = startOfDay(subDays(today, 1));
        newEndDate = endOfDay(subDays(today, 1));
        newPeriod = 'daily';
        break;
      case 'This Week':
        newStartDate = startOfWeek(today, { weekStartsOn: 1 });
        newEndDate = endOfWeek(today, { weekStartsOn: 1 });
        newPeriod = 'weekly';
        break;
      case 'Last Week':
        newStartDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        newEndDate = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        newPeriod = 'weekly';
        break;
      case 'This Month':
        newStartDate = startOfMonth(today);
        newEndDate = endOfMonth(today);
        newPeriod = 'monthly';
        break;
      case 'Last Month':
        newStartDate = startOfMonth(subMonths(today, 1));
        newEndDate = endOfMonth(subMonths(today, 1));
        newPeriod = 'monthly';
        break;
      default:
        return;
    }

    setSelectedPreset(preset);
    setShowCustomRangePickers(false);
    setCustomRangePopoverOpen(false);
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);
    onDateRangeChange(newStartDate, newEndDate);
    onPeriodChange(newPeriod);
  };

  const handleCustomStartDateChange = (date) => {
    const newStartDate = date ? startOfDay(date) : null;
    setLocalStartDate(newStartDate);
    onDateRangeChange(newStartDate, localEndDate);
    if (!period || period !== 'daily') onPeriodChange('daily');
  };

  const handleCustomEndDateChange = (date) => {
    const newEndDate = date ? endOfDay(date) : null;
    setLocalEndDate(newEndDate);
    onDateRangeChange(localStartDate, newEndDate);
    if (!period || period !== 'daily') onPeriodChange('daily');
  };

  const applyCustomRange = () => {
    setCustomRangePopoverOpen(false);
    onDateRangeChange(localStartDate, localEndDate);
  };

  // Initialize default values if none provided
  useEffect(() => {
    if (!startDate && !endDate && !showCustomRangePickers && !customRangePopoverOpen) {
      const initialPreset = 'This Month';
      setSelectedPreset(initialPreset);
      const today = new Date();
      const newStartDate = startOfMonth(today);
      const newEndDate = endOfMonth(today);

      setLocalStartDate(newStartDate);
      setLocalEndDate(newEndDate);

      if (typeof onDateRangeChange === 'function') {
        onDateRangeChange(newStartDate, newEndDate);
      }
      if (typeof onPeriodChange === 'function') {
        onPeriodChange('monthly');
      }
      return;
    }

    // Handle preset detection
    if (startDate && endDate) {
      const today = new Date();
      let matchedPreset = null;

      if (isEqual(startOfDay(startDate), startOfDay(today)) &&
        isEqual(endOfDay(endDate), endOfDay(today))) {
        matchedPreset = 'Today';
      } else if (isEqual(startOfDay(startDate), startOfDay(subDays(today, 1))) &&
        isEqual(endOfDay(endDate), endOfDay(subDays(today, 1)))) {
        matchedPreset = 'Yesterday';
      } else if (isEqual(startOfDay(startDate), startOfWeek(today, { weekStartsOn: 1 })) &&
        isEqual(endOfDay(endDate), endOfWeek(today, { weekStartsOn: 1 }))) {
        matchedPreset = 'This Week';
      } else if (isEqual(startOfDay(startDate), startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 })) &&
        isEqual(endOfDay(endDate), endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }))) {
        matchedPreset = 'Last Week';
      } else if (isEqual(startOfDay(startDate), startOfMonth(today)) &&
        isEqual(endOfDay(endDate), endOfMonth(today))) {
        matchedPreset = 'This Month';
      } else if (isEqual(startOfDay(startDate), startOfMonth(subMonths(today, 1))) &&
        isEqual(endOfDay(endDate), endOfMonth(subMonths(today, 1)))) {
        matchedPreset = 'Last Month';
      }

      if (matchedPreset) {
        setSelectedPreset(matchedPreset);
      } else {
        setSelectedPreset('Custom Range');
      }
    }
  }, [startDate, endDate, onDateRangeChange, onPeriodChange, showCustomRangePickers, customRangePopoverOpen, period]);

  // Format display text for the dropdown
  let currentDropdownText = selectedPreset;
  if (selectedPreset === 'Custom Range' && localStartDate && localEndDate) {
    currentDropdownText = `${format(localStartDate, 'MMM d, yyyy')} - ${format(localEndDate, 'MMM d, yyyy')}`;
  }

  const renderCustomDatePickers = (isPopoverContext = false) => (
    <div className={`${isPopoverContext ? 'custom-date-picker-container' : 'mt-2'}`}>
      <Row className="g-2">
        <Col xs={12} sm={isPopoverContext ? 12 : 6}>
          <DatePicker
            selected={localStartDate}
            onChange={handleCustomStartDateChange}
            selectsStart
            startDate={localStartDate}
            endDate={localEndDate}
            className="form-control w-100"
            placeholderText="Start Date"
            dateFormat="MMM d, yyyy"
            popperPlacement="auto"
            wrapperClassName="w-100"
          />
        </Col>
        <Col xs={12} sm={isPopoverContext ? 12 : 6}>
          <DatePicker
            selected={localEndDate}
            onChange={handleCustomEndDateChange}
            selectsEnd
            startDate={localStartDate}
            endDate={localEndDate}
            minDate={localStartDate}
            className="form-control w-100"
            placeholderText="End Date"
            dateFormat="MMM d, yyyy"
            popperPlacement="auto"
            wrapperClassName="w-100"
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <div className="date-range-preset-filter">
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle color="primary" caret innerRef={dropdownToggleRef} id="dateRangeDropdownToggle">
          {currentDropdownText}
        </DropdownToggle>
        <DropdownMenu>
          {['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month'].map(p => (
            <DropdownItem
              key={p}
              onClick={() => applyPreset(p)}
              active={selectedPreset === p && !showCustomRangePickers && !customRangePopoverOpen}
            >
              {p}
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem
            onClick={() => applyPreset('Custom Range')}
            active={selectedPreset === 'Custom Range' || showCustomRangePickers || customRangePopoverOpen}
          >
            Custom Range
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {!usePopoverForCustomRange && showCustomRangePickers && (
        <Card className="mt-2 shadow-sm">
          <CardBody className="p-3">
            {renderCustomDatePickers()}
            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" size="sm" onClick={() => setShowCustomRangePickers(false)}>
                Apply
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {customRangePopoverOpen && (
        <Popover
          placement="bottom"
          isOpen={customRangePopoverOpen}
          target="dateRangeDropdownToggle"
          toggle={() => setCustomRangePopoverOpen(!customRangePopoverOpen)}
          trigger="legacy"
          fade={false}
        >
          <PopoverHeader>Select Custom Range</PopoverHeader>
          <PopoverBody className="p-3">
            {renderCustomDatePickers(true)}
            <div className="d-flex justify-content-end mt-3">
              <Button color="secondary" size="sm" className="me-2" onClick={() => setCustomRangePopoverOpen(false)}>
                Close
              </Button>

            </div>
          </PopoverBody>
        </Popover>
      )}
    </div>
  );
};

export default DateRangePresetFilter;